import { ActionIcon, Menu, Text } from "@mantine/core"
import {
  IconDotsVertical,
  IconFolderOff,
  IconQrcode,
  IconRocket,
  IconFileArrowLeft,
  IconDownload,
  IconTableImport,
  IconRefresh,
} from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing } from "@tfg/ui"
import { useMemo } from "react"

import { DocumentDownload } from "~/components/DocumentDownload"
import { DateCell } from "~/components/cells/DateCell"
import { FindIngestionsResponseDto } from "~/gen/api"
import { IngestionStatusBadge } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionStatusBadge"
import { useExportProductQrCodeMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useExportProductQrCodeMutation"
import { useRetryAction } from "~/pages/Admin/Ingestion/ProductImport/hooks/useRetryAction"
import { useRevertImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useRevertImportMutation"

type Props = {
  handleDrawerOpen: (ingestion: FindIngestionsResponseDto) => void
  handleErrorModalOpen: (ingestionId: string) => void
  handleImportModalOpen: (
    ingestion: FindIngestionsResponseDto,
    isBlankIngestion: boolean
  ) => void
}

export const useIngestionListColumns = ({
  handleDrawerOpen,
  handleErrorModalOpen,
  handleImportModalOpen,
}: Props) => {
  const columnHelper = createColumnHelper<FindIngestionsResponseDto>()

  const { mutate: revertImport } = useRevertImportMutation()
  const { mutate: exportProductQrCode } = useExportProductQrCodeMutation()

  const getRetryAction = useRetryAction({
    handleDrawerOpen,
    handleImportModalOpen,
  })

  return useMemo<ColumnDef<FindIngestionsResponseDto, any>[]>(
    () => [
      columnHelper.accessor("createdAt", {
        header: "Created at",
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("createdBy.email", {
        header: "Email",
        cell: (info) => <Text fz="xs">{info.getValue()}</Text>,
      }),
      columnHelper.accessor("document.originalFileName", {
        header: "File Name",
        cell: (info) => {
          const value = info.getValue()
          return value ? <Text fz="xs">{info.getValue()}</Text> : <Missing />
        },
      }),
      columnHelper.accessor("product_count", {
        header: "No. products ingested",
        cell: (info) => <Text fz="xs">{info.getValue()}</Text>,
      }),
      columnHelper.accessor("traceabilityLaunchedAt", {
        header: "Traceability Launch date",
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          return <IngestionStatusBadge status={info.getValue()} />
        },
      }),
      columnHelper.display({
        id: "launch",
        cell: ({ row }) => {
          const retryAction = getRetryAction(row.original)
          return (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon>
                  <IconDotsVertical />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {row.original.status === "PRODUCTS_INGESTED" ? (
                  <>
                    <Menu.Item
                      onClick={() => handleDrawerOpen(row.original)}
                      leftSection={<IconRocket />}
                    >
                      <Text fz="sm">Launch Traceability</Text>
                    </Menu.Item>
                    <Menu.Item
                      onClick={() =>
                        revertImport({ ingestionId: row.original.id })
                      }
                      leftSection={<IconFolderOff />}
                    >
                      <Text fz="sm">Revert Import</Text>
                    </Menu.Item>
                  </>
                ) : null}

                {row.original.status === "BLANK_PRODUCTS_INGESTED" ? (
                  <>
                    <Menu.Item
                      onClick={() =>
                        exportProductQrCode({
                          ingestionId: row.original.id,
                        })
                      }
                      leftSection={<IconQrcode />}
                    >
                      <Text fz="sm">Export Qr Code</Text>
                    </Menu.Item>
                  </>
                ) : null}

                {row.original.status === "ERROR" ? (
                  <>
                    <Menu.Item
                      leftSection={<IconFileArrowLeft />}
                      onClick={() => handleErrorModalOpen(row.original.id)}
                    >
                      <Text fz="sm">Show error logs</Text>
                    </Menu.Item>
                  </>
                ) : null}

                {row.original.status === "BLANK_PRODUCTS_VERIFIED" ? (
                  <Menu.Item
                    leftSection={<IconTableImport />}
                    onClick={() => handleImportModalOpen(row.original, true)}
                  >
                    <Text fz="sm">Import blank products</Text>
                  </Menu.Item>
                ) : null}

                {row.original.status === "PRODUCTS_VERIFIED" ? (
                  <Menu.Item
                    leftSection={<IconTableImport />}
                    onClick={() => handleImportModalOpen(row.original, false)}
                  >
                    <Text fz="sm">Import products</Text>
                  </Menu.Item>
                ) : null}

                {["ERROR", "FAILED"].includes(row.original.status) &&
                row.original.step &&
                retryAction ? (
                  <>
                    <Menu.Item
                      leftSection={<IconRefresh />}
                      onClick={() => retryAction.trigger(row.original)}
                    >
                      <Text fz="sm">{retryAction.name}</Text>
                    </Menu.Item>
                  </>
                ) : null}

                <Menu.Item leftSection={<IconDownload />}>
                  <DocumentDownload
                    documentId={row.original.document.id}
                    documentName={row.original.document.originalFileName ?? ""}
                    showAsText={true}
                    customText="Download product import file"
                  />
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )
        },
      }),
    ],
    [
      columnHelper,
      exportProductQrCode,
      handleDrawerOpen,
      handleErrorModalOpen,
      revertImport,
      handleImportModalOpen,
      getRetryAction,
    ]
  )
}
